<template>
  <div class="duplicate-guest-review">
    <p class="tool-description">
      Priority 1 de-duplication (Phase F). Candidates the matching ladder and backfill
      found, sorted by confidence - exact Zaui id collisions (1.0) are safe to
      auto-approve; everything else is a judgment call. Approving merges the loser
      into the winner and can be undone; rejecting just clears it from this list.
    </p>

    <div v-if="loading" class="loading-message">Loading candidates...</div>

    <div v-else>
      <div class="summary-bar">
        <span><strong>{{ mergeable.length }}</strong> candidate(s) to review</span>
        <span v-if="flagged.length">· <strong>{{ flagged.length }}</strong> flagged (id matched, name looks wrong - not mergeable, needs data review)</span>
        <span v-if="staffGuard.length">· <strong>{{ staffGuard.length }}</strong> involve a staff record (never auto-merged)</span>
        <button class="refresh-button" @click="loadAll">Refresh</button>
      </div>

      <div v-if="mergeable.length === 0" class="empty-message">No open merge candidates.</div>

      <table v-else class="candidate-table">
        <thead>
          <tr>
            <th>Confidence</th>
            <th>Match type</th>
            <th>Person A</th>
            <th>Person B</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="candidate in mergeable">
            <tr :key="candidateKey(candidate)" :class="{ selected: selected === candidate }">
              <td>
                <span class="confidence-badge" :class="confidenceClass(candidate.confidence)">{{ candidate.confidence.toFixed(1) }}</span>
              </td>
              <td>
                {{ candidate.matchType }}
                <span class="info-icon" tabindex="0">
                  <span class="info-icon-symbol">?</span>
                  <span class="info-tooltip">{{ matchTypeDescription(candidate.matchType) }}</span>
                </span>
              </td>
              <td>
                {{ personBadge(candidate, candidate.personA.personid) }}{{ candidate.personA.firstname }} {{ candidate.personA.lastname }} <br>
                <small>{{ candidate.personA.email }}</small> <br>
                <small class="last-reservation">last flown: {{ formatDateOnly(candidate.personA.lastReservationOn) || 'never' }}</small>
              </td>
              <td>
                {{ personBadge(candidate, candidate.personB.personid) }}{{ candidate.personB.firstname }} {{ candidate.personB.lastname }} <br>
                <small>{{ candidate.personB.email }}</small> <br>
                <small class="last-reservation">last flown: {{ formatDateOnly(candidate.personB.lastReservationOn) || 'never' }}</small>
              </td>
              <td>
                <button class="preview-button" @click="togglePreview(candidate)">{{ selected === candidate ? 'Hide' : 'Preview' }}</button>
                <button class="reject-button" @click="reject(candidate)" :disabled="busy">Reject</button>
              </td>
            </tr>
            <tr v-if="selected === candidate" :key="candidateKey(candidate) + '-preview'">
              <td colspan="5">
                <div class="preview-panel">
                  <div v-if="previewLoading" class="loading-message">Loading preview...</div>
                  <div v-else-if="previewError" class="error-message">{{ previewError }}</div>
                  <div v-else-if="preview">
                    <p>
                      Winner: <strong>{{ preview.winner.firstname }} {{ preview.winner.lastname }}</strong> (personid {{ preview.winner.personid }})
                      &nbsp;·&nbsp; Loser: <strong>{{ preview.loser.firstname }} {{ preview.loser.lastname }}</strong> (personid {{ preview.loser.personid }})
                      <button class="swap-button" @click="swapWinnerLoser" :disabled="previewLoading">⇄ Swap</button>
                    </p>
                    <div class="field-policy-toggle">
                      Contact/name fields:
                      <button
                        class="mode-button"
                        :class="{ active: mergeMode === 'recency' }"
                        :disabled="previewLoading"
                        @click="setMergeMode('recency')"
                      >Most recent sync wins</button>
                      <button
                        class="mode-button"
                        :class="{ active: mergeMode === 'manual' }"
                        :disabled="previewLoading"
                        @click="setMergeMode('manual')"
                      >Keep winner's (manual)</button>
                      <span v-if="mergeMode === 'recency'" class="mode-hint">
                        winner synced {{ formatDateOnly(preview.winnerSyncedOn) || 'never' }}, loser synced {{ formatDateOnly(preview.loserSyncedOn) || 'never' }} - whichever is more recent wins each field below
                      </span>
                      <span v-else class="mode-hint">winner's values are kept as-is; a field only fills in from the loser if the winner's is blank</span>
                    </div>
                    <div v-if="preview.refusalReason" class="refusal-banner">
                      This merge would be refused: <strong>{{ preview.refusalReason }}</strong>
                      <span v-if="preview.refusalReason === 'loser-has-user-login'" class="refusal-hint"> - swapping winner/loser may help here, since the staff record would then survive as the winner instead of being merged away.</span>
                    </div>
                    <template v-else>
                      <ul class="preview-summary">
                        <li v-if="preview.tripClientMoves.length">{{ preview.tripClientMoves.length }} trip assignment(s) move to the winner</li>
                        <li v-if="preview.tripClientCollisions.length">{{ preview.tripClientCollisions.length }} same-date collision(s) - one row retired each, equipment follows</li>
                        <li v-if="preview.reservationMoves.length">{{ preview.reservationMoves.length }} reservation(s) move (ghosts included)</li>
                        <li v-if="preview.notesMoves.length">{{ preview.notesMoves.length }} note(s) move</li>
                        <li v-if="preview.wildlifeMoves.length">{{ preview.wildlifeMoves.length }} wildlife sighting(s) move</li>
                        <li v-if="preview.healthPlan.duplicatesDiscarded.length">{{ preview.healthPlan.duplicatesDiscarded.length }} duplicate health record(s) discarded (less severe one)</li>
                        <li v-if="preview.trainingPlan.duplicatesDiscarded.length">{{ preview.trainingPlan.duplicatesDiscarded.length }} duplicate training record(s) discarded (later date)</li>
                        <li v-if="preview.customFieldsPlan.duplicatesDiscarded.length">{{ preview.customFieldsPlan.duplicatesDiscarded.length }} duplicate custom field(s) discarded (older value)</li>
                        <li v-if="Object.keys(preview.fieldPolicy).length === 0" class="no-field-changes">No contact/name/weight fields change - winner's values are kept.</li>
                        <li v-for="(change, field) in preview.fieldPolicy" :key="field">
                          <strong>{{ field }}</strong> on the winner changes: "{{ change.before }}" &rarr; "{{ change.after }}" &mdash; taken from the <strong>{{ change.source }}</strong>
                        </li>
                      </ul>
                      <button class="approve-button" @click="approve(candidate)" :disabled="busy">Approve merge</button>
                    </template>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <div v-if="flagged.length" class="collapsible-subsection">
        <div class="header" @click="showFlagged = !showFlagged">
          <h4>Flagged (not mergeable)</h4>
          <span>{{ showFlagged ? '▲' : '▼' }}</span>
        </div>
        <div v-if="showFlagged" class="content">
          <p class="tool-description">A Zaui id matched but the name on file looks materially different - this means <em>we</em> stamped the wrong row at some point, not that Zaui shares ids. Needs a data-quality look, not a merge.</p>
          <ul>
            <li v-for="c in flagged" :key="c.candidateId">
              personid {{ c.personA.personid }} ({{ c.personA.firstname }} {{ c.personA.lastname }}) - stored name vs incoming booking name mismatch. <a href="#" @click.prevent="reject(c)">Dismiss</a>
            </li>
          </ul>
        </div>
      </div>

      <div class="collapsible-subsection">
        <div class="header" @click="showRecent = !showRecent; if (showRecent && recentMerges.length === 0) loadRecent()">
          <h4>Recent merges</h4>
          <span>{{ showRecent ? '▲' : '▼' }}</span>
        </div>
        <div v-if="showRecent" class="content">
          <table v-if="recentMerges.length" class="candidate-table">
            <thead>
              <tr><th>When</th><th>Loser &rarr; Winner</th><th>By</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="m in recentMerges" :key="m.mergeId">
                <td>{{ formatDate(m.performedAt) }}</td>
                <td>{{ m.loserName }} (#{{ m.loserPersonId }}) &rarr; {{ m.winnerName }} (#{{ m.winnerPersonId }})</td>
                <td>{{ m.performedByName }}</td>
                <td>{{ m.undoneAt ? 'undone' : 'merged' }}</td>
                <td><button v-if="!m.undoneAt" class="reject-button" @click="undo(m)" :disabled="busy">Undo</button></td>
              </tr>
            </tbody>
          </table>
          <p v-else>No merges yet.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataManagementService from "@/services/DataManagementService";

export default {
  name: "DuplicateGuestReview",
  data() {
    return {
      loading: true,
      busy: false,
      mergeable: [],
      staffGuard: [],
      flagged: [],
      selected: null,
      preview: null,
      previewLoading: false,
      previewError: null,
      previewWinnerId: null,
      previewLoserId: null,
      mergeMode: 'recency',
      showFlagged: false,
      showRecent: false,
      recentMerges: [],
    };
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    matchTypeDescription(matchType) {
      const descriptions = {
        'backfill-id-collision': 'Two person records independently resolved to the same real Zaui client id during the historical id backfill - the strongest possible signal, the same human with the same Zaui account. Safe to auto-merge.',
        'integrity-alarm': "A Zaui id matched exactly, but the stored name looks materially different from the incoming booking's name. Usually means the id was stamped onto the wrong row at some point - needs a data review, not a merge.",
        'legacy-ineligible': "Email, first name, and last name matched exactly, but the older record has no real Zaui-linked reservation history (a ghost-only or hand-added client) - too risky to auto-attach on its own, so it's here for a human to confirm.",
        'id-conflict': 'Email, first name, and last name matched exactly, but the older record already holds a different Zaui id. Worth checking these are genuinely the same person before merging.',
        'lastname-dob': 'Same last name and date of birth (first name also matches, where flagged) - a common pattern worth reviewing, but weaker than an id or full name+email match.',
        'staff-guard': "Involves a person with a staff record. Never auto-merged - merging a guest's duplicate onto a staff member's record could corrupt their employee data.",
      };
      return descriptions[matchType] || 'No description available for this match type.';
    },
    candidateKey(c) {
      return (c.candidateId || 'swept') + '-' + c.personA.personid + '-' + c.personB.personid;
    },
    confidenceClass(confidence) {
      if (confidence >= 1.0) return 'confidence-high';
      if (confidence >= 0.6) return 'confidence-medium';
      return 'confidence-low';
    },
    formatDate(value) {
      if (!value) return '';
      const d = new Date(value);
      return isNaN(d.getTime()) ? '' : d.toLocaleString();
    },
    formatDateOnly(value) {
      if (!value) return '';
      // A plain "YYYY-MM-DD" DATEONLY string (e.g. a reservation's activitydate) gets parsed by
      // `new Date()` as UTC midnight - `toLocaleDateString()` then renders that in the browser's
      // local timezone, silently shifting the displayed date back a day west of UTC (this is what
      // made merge-review "last flown" dates look a day earlier than the actual reservation).
      // Parse those by hand. A real timestamp (e.g. a Zaui sync time) already carries zone info
      // and converts correctly, so only the bare-date case needs the workaround.
      const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (dateOnlyMatch) {
        return `${Number(dateOnlyMatch[2])}/${Number(dateOnlyMatch[3])}/${dateOnlyMatch[1]}`;
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
    },
    async loadAll() {
      this.loading = true;
      try {
        const response = await DataManagementService.getMergeCandidates();
        this.mergeable = response.data.mergeable;
        this.staffGuard = response.data.staffGuard;
        this.flagged = response.data.flagged;
      } catch (error) {
        console.error("Error loading merge candidates:", error);
        alert("Failed to load merge candidates: " + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },
    async loadRecent() {
      try {
        const response = await DataManagementService.getRecentMerges();
        this.recentMerges = response.data;
      } catch (error) {
        console.error("Error loading recent merges:", error);
      }
    },
    defaultWinnerLoser(candidate) {
      // An exact id collision already has a correct answer - whoever holds the
      // zauiclientid is the winner. Anything else has no natural default, but still
      // needs a starting point for the preview; A/B order is as good as any until
      // the reviewer swaps it.
      const winnerId = candidate.personB.zauiclientid ? candidate.personB.personid : candidate.personA.personid;
      const loserId = candidate.personB.zauiclientid ? candidate.personA.personid : candidate.personB.personid;
      return { winnerId, loserId };
    },
    personBadge(candidate, personId) {
      if (this.selected !== candidate || !this.preview) return '';
      if (personId === this.previewWinnerId) return '👑 ';
      if (personId === this.previewLoserId) return '💀 ';
      return '';
    },
    async togglePreview(candidate) {
      if (this.selected === candidate) {
        this.selected = null;
        this.preview = null;
        this.previewWinnerId = null;
        this.previewLoserId = null;
        return;
      }
      this.selected = candidate;
      this.mergeMode = 'recency';
      const { winnerId, loserId } = this.defaultWinnerLoser(candidate);
      this.previewWinnerId = winnerId;
      this.previewLoserId = loserId;
      await this.fetchPreview();
    },
    async fetchPreview() {
      this.preview = null;
      this.previewError = null;
      this.previewLoading = true;
      try {
        const response = await DataManagementService.previewMerge(this.previewWinnerId, this.previewLoserId, this.mergeMode);
        this.preview = response.data;
      } catch (error) {
        console.error("Error previewing merge:", error);
        this.previewError = error.response?.data?.message || error.message;
      } finally {
        this.previewLoading = false;
      }
    },
    async setMergeMode(mode) {
      if (this.mergeMode === mode) return;
      this.mergeMode = mode;
      await this.fetchPreview();
    },
    async swapWinnerLoser() {
      const winnerId = this.previewLoserId;
      const loserId = this.previewWinnerId;
      this.previewWinnerId = winnerId;
      this.previewLoserId = loserId;
      await this.fetchPreview();
    },
    async approve(candidate) {
      const winnerId = this.previewWinnerId;
      const loserId = this.previewLoserId;
      const winnerName = this.preview && this.preview.winner ? `${this.preview.winner.firstname} ${this.preview.winner.lastname}` : winnerId;
      const loserName = this.preview && this.preview.loser ? `${this.preview.loser.firstname} ${this.preview.loser.lastname}` : loserId;
      if (!confirm(`Merge ${loserName} into ${winnerName}? (${loserName} will be soft-deleted.) This can be undone from Recent merges.`)) return;
      this.busy = true;
      try {
        await DataManagementService.performMerge(winnerId, loserId, candidate.candidateId, this.mergeMode);
        this.selected = null;
        this.preview = null;
        this.previewWinnerId = null;
        this.previewLoserId = null;
        this.mergeMode = 'recency';
        await this.loadAll();
      } catch (error) {
        console.error("Error performing merge:", error);
        alert("Merge failed: " + (error.response?.data?.message || error.message));
      } finally {
        this.busy = false;
      }
    },
    async reject(candidate) {
      if (!candidate.candidateId) {
        alert("This candidate was found by a live sweep, not filed in the review queue, so there's nothing to dismiss - it just won't be suggested as strongly next time unless the underlying data changes.");
        return;
      }
      this.busy = true;
      try {
        await DataManagementService.dismissCandidate(candidate.candidateId);
        if (this.selected === candidate) { this.selected = null; this.preview = null; this.previewWinnerId = null; this.previewLoserId = null; }
        await this.loadAll();
      } catch (error) {
        console.error("Error dismissing candidate:", error);
        alert("Dismiss failed: " + (error.response?.data?.message || error.message));
      } finally {
        this.busy = false;
      }
    },
    async undo(mergeEntry) {
      if (!confirm(`Undo the merge of ${mergeEntry.loserName} into ${mergeEntry.winnerName}?`)) return;
      this.busy = true;
      try {
        await DataManagementService.undoMerge(mergeEntry.mergeId);
        await this.loadRecent();
        await this.loadAll();
      } catch (error) {
        console.error("Error undoing merge:", error);
        alert("Undo failed: " + (error.response?.data?.message || error.message));
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style scoped>
.tool-description {
  color: #555;
  margin-bottom: 15px;
}

.summary-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
}

.refresh-button {
  margin-left: auto;
}

.empty-message, .loading-message {
  padding: 20px;
  color: #666;
  text-align: center;
}

.error-message {
  color: #a94442;
}

.candidate-table {
  width: 100%;
  border-collapse: collapse;
}

.candidate-table th, .candidate-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  vertical-align: top;
}

.candidate-table thead {
  background-color: #f5f5f5;
}

tr.selected {
  background-color: #f0f7ff;
}

.confidence-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: bold;
  font-size: 0.85em;
}

.confidence-high { background-color: #d4edda; color: #155724; }
.confidence-medium { background-color: #fff3cd; color: #856404; }
.confidence-low { background-color: #f8d7da; color: #721c24; }

.preview-panel {
  padding: 10px;
  background-color: #fafafa;
  border-radius: 4px;
}

.preview-summary {
  margin: 10px 0;
}

.refusal-banner {
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 10px;
}

.refusal-hint {
  font-style: italic;
}

.swap-button {
  margin-left: 10px;
  padding: 3px 10px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
}

.field-policy-toggle {
  margin-bottom: 10px;
  padding: 8px 10px;
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 0.9em;
}

.mode-button {
  margin-left: 6px;
  padding: 3px 10px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff;
  border: 1px solid #ced4da;
}

.mode-button.active {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.mode-hint {
  display: block;
  margin-top: 4px;
  color: #666;
  font-style: italic;
}

.no-field-changes {
  color: #666;
}

.approve-button {
  background-color: #28a745;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 4px;
  cursor: pointer;
}

.reject-button {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 4px;
}

.preview-button {
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.collapsible-subsection {
  margin-top: 20px;
  border: 1px solid #ccc;
  border-radius: 5px;
  overflow: hidden;
}

.collapsible-subsection .header {
  background-color: #f5f5f5;
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapsible-subsection .content {
  padding: 10px;
}

.last-reservation {
  color: #888;
}

.info-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: #6c757d;
  color: white;
  font-size: 10px;
  font-weight: bold;
  cursor: help;
  margin-left: 5px;
  vertical-align: middle;
}

.info-icon-symbol {
  pointer-events: none;
}

.info-tooltip {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 130%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #fff;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: normal;
  line-height: 1.3;
  width: 240px;
  z-index: 20;
  transition: opacity 0.15s ease-in-out;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

.info-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.info-icon:hover .info-tooltip,
.info-icon:focus .info-tooltip {
  visibility: visible;
  opacity: 1;
}
</style>
